// Expanding a recurring lesson series into dated occurrences.
//
// Two constraints shape this. MMS lets a series "repeat indefinitely", so the
// occurrence list is unbounded and cannot be materialized up front — it is
// generated on demand for whatever window the caller needs. And the app has to
// work offline, so expansion is a pure local computation over a small rule
// document rather than a server query.
//
// Dates are handled as YYYY-MM-DD calendar strings with a separate HH:mm local
// time, never as instants. A weekly 4:00 PM lesson is at 4:00 PM on both sides
// of a daylight-saving boundary; storing it as an absolute timestamp and adding
// 7*24h would drift it by an hour twice a year. All arithmetic below runs on
// UTC midnights purely as a stable calendar grid, which is immune to DST.


// ── The expansion is `core/src/recurrence.rs` (#214) ───────────────────────
//
// What is in this file is the **document shape**: the vocabulary a stored
// rule uses (`RECURRENCE_MODES`, `FREQUENCIES`, `FREQUENCY_UNITS`,
// `MONTHLY_ANCHORS`), the form helpers that decide which two fields a form
// writes, and the flattening below. The walk itself, the monthly clamping, the
// nth-weekday anchor and the three rule comparisons are the core's.
//
// `src/domain/conflicts.js` and `src/domain/schedule.js` both import from this
// module — `expandSeries`, `occursOn`, `addDays`, `producesEntriesAfter`,
// `MAX_OCCURRENCES`, `RECURRENCE_MODES`.

// `./core.js`, not `../lib/core.js`: this directory is copied verbatim into
// functions/domain/, and the door is inside the copy (#218).
import { coreSync } from './core.js';

// ── Shaping a stored rule into what the boundary takes ─────────────────────
//
// The boundary takes a rule flat — scalars, with `exceptions` as its own
// positional list — because a list with exactly one parent is flattened rather
// than nested (`core-ffi`'s flat rule). A stored rule is a document with seven
// fields. Converting between the two is shaping, which stays in JavaScript
// (#214).
//
// **Every number is coerced here rather than at the boundary.** A `byWeekday`
// read off Firestore may be a string in an older document, and the boundary
// takes an `i32`: handing it `"1"` throws inside the wasm glue with a message
// that names neither the field nor this file.
const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const exceptionsOf = (rule) => (Array.isArray(rule?.exceptions)
  ? rule.exceptions.filter((d) => typeof d === 'string')
  : []);

/** The six scalars, as the boundary's `RecurrenceRule` record. */
const scalars = (rule) => ({
  startDate: rule?.startDate ?? null,
  frequency: rule?.frequency ?? null,
  interval: numOrNull(rule?.interval),
  endDate: rule?.endDate ?? null,
  byWeekday: numOrNull(rule?.byWeekday),
  weekOfMonth: numOrNull(rule?.weekOfMonth),
});

/** The same six, positionally, for the one-rule calls. */
const flat = (rule) => [
  rule?.startDate ?? null,
  rule?.frequency ?? null,
  numOrNull(rule?.interval),
  rule?.endDate ?? null,
  exceptionsOf(rule),
  numOrNull(rule?.byWeekday),
  numOrNull(rule?.weekOfMonth),
];

// How a series repeats. A one-time event is a series of length one, and a
// temporary weekly hold is a short-lived recurring series — modelling all three
// the same way means the expansion has one code path rather than three.
export const RECURRENCE_MODES = {
  one_time: 'one_time',
  weekly_hold: 'weekly_hold',
  recurring: 'recurring',
};




export function addDays(iso, n) {
  return coreSync().addDays(iso, n);
}

export function dayOfWeek(iso) {
  return coreSync().dayOfWeek(iso);
}

export function monthKey(iso) {
  return coreSync().monthKey(iso);
}

// The frequency list MMS offers, in MMS's order. Not every spelling the core's
// `stride_of` understands: MMS has no Biweekly, modelling a fortnight as Weekly
// with "Repeats Every 2" (#26). Stored `biweekly` rules still expand (see
// `stride_of` in `core/src/recurrence.rs`); the form never writes one.
export const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];

// What "Repeats Every N ___" counts in, one per entry above.
//
// MMS puts the unit beside the number as a suffix that follows the Frequency
// select — "1  Week(s)" while Weekly is chosen — rather than folding the
// interval into the option labels
// (research/mms-capture/03-modals/cal-04-recurrence-expanded.jpg and
// exp-02-recurrence.jpg).
export const FREQUENCY_UNITS = {
  daily: 'Day(s)',
  weekly: 'Week(s)',
  monthly: 'Month(s)',
  yearly: 'Year(s)',
};

// ── Monthly on day 14, or monthly on the second Monday (#26, #78) ──────────
//
// MMS's monthly recurrence can be anchored to a day *number* or to an ordinal
// weekday: "Monthly on day 14" and "Monthly on the second Monday". The weekday
// anchor is two fields, `byWeekday` (0 Sun .. 6 Sat, as `dayOfWeek` above) and
// `weekOfMonth` (1..5, or -1 for "the last"); a rule carrying only one of them
// falls back to day-of-month. The pair is read by `nth_of` in
// `core/src/events.rs` (for `monthlyAnchorOf` below) and in
// `core/src/recurrence.rs` (for the expansion).
//
// A fifth stays a fifth: on a fifth-Monday start date MMS offers "Monthly on
// the fifth Monday", so a fifth-Monday and a last-Monday rule are two rules and
// do not compare equal. A fifth-Monday series *skips* a month with only four
// Mondays (RFC 5545's `BYDAY=5MO`), as MMS's does, where a last-Monday one
// lands on the fourth — `nth_when_the_month_is_short` in
// `core/src/recurrence.rs`. An ordinal above five lands on the largest that
// exists; firestore.rules refuses to store one
// (`optionalIntInRange(d,'weekOfMonth',-1,5)`).
//
// The day-of-month anchor clamps instead: a series starting Jan 31 lands on
// Feb 28. What MMS does with a day-31 series in February is not covered by
// `research/mms-capture/`.

/** A stored anchor half as the core reads it: absent or emptied is nothing. */
const anchorHalf = (v) => (v == null || v === '' ? null : Number(v));

// The nth-weekday rule a date is itself an instance of.
//
// MMS derives the offer from the date already in the form — pick the 14th and
// it offers "the second Monday". The ordinal is the day number over seven,
// rounded up. A fifth is offered as "the fifth", never as "the last" (#78), and
// a fourth stays the fourth even where it is the month's final one. Nothing
// here writes -1, though the expansion honours a stored one.
export function nthWeekdayOf(dateISO) {
  // Two core calls: the boundary returns the ordinal as a scalar (the weekday
  // half is `day_of_week`), and assembling the `{ byWeekday, weekOfMonth }`
  // pair a form writes is shaping, this file's job.
  const ordinal = coreSync().nthWeekdayOrdinalOf(dateISO);
  if (ordinal === null || ordinal === undefined) return null;
  return { byWeekday: coreSync().dayOfWeek(dateISO), weekOfMonth: ordinal };
}

// "the second Monday", for the option label and for anything quoting the rule
// back at a teacher. Null when the rule is not an nth-weekday one.
export function nthWeekdayLabel(rule) {
  return coreSync().nthWeekdayLabel(
    numOrNull(rule?.byWeekday), numOrNull(rule?.weekOfMonth),
  ) ?? null;
}

// Which of the two monthly anchors a stored rule uses. The form's select reads
// this to open on the option the rule was saved with.
export const MONTHLY_ANCHORS = { day: 'day', weekday: 'weekday' };

export function monthlyAnchorOf(rule) {
  return coreSync().monthlyAnchorOf(anchorHalf(rule?.byWeekday), anchorHalf(rule?.weekOfMonth));
}

// The two fields a form writes for its chosen anchor.
//
// Null on every frequency but monthly, and null for the day-of-month anchor.
// Leaving them set would mean switching a rule to weekly and back silently
// restored a weekday nobody re-chose — and `expandSeries` would then honour it,
// because a rule is read from what it stores rather than from what the form
// last showed. Always written as a *pair*, since the core's `nth_of` refuses a
// rule carrying one of them.
export function monthlyAnchorFields(frequency, anchor, dateISO) {
  const text = (v) => (v == null ? null : String(v));
  const pair = coreSync().monthlyAnchorFields(text(frequency), text(anchor), text(dateISO));
  return { byWeekday: pair.byWeekday ?? null, weekOfMonth: pair.weekOfMonth ?? null };
}

// A stored rule in the spelling the form offers.
//
// A stored `biweekly` is not in the frequency list, so a select would fall
// back to its first option — Daily — and a teacher changing a fortnightly
// series' end date would save a daily one. Reading it back as weekly-every-2
// shows the same fortnight in a spelling the form has, and the core's
// `stride_of` calls the two one rule, so the save is not a reschedule either.
export function formRecurrence(frequency, interval) {
  const text = (v) => (v == null ? null : String(v));
  const r = coreSync().formRecurrence(text(frequency), text(interval));
  return { frequency: r.frequency, interval: Number(r.interval) };
}

// A hard ceiling on how many occurrences one expansion call will return.
// Indefinite series are infinite by definition; without a cap a caller that
// passes a wide window (or a corrupted rule with interval 0) hangs the tab.
export const MAX_OCCURRENCES = 1000;

// Whether the last expansion was cut short by the cap. A caller rendering a
// calendar month can ignore this; a caller building a *report* must not, because
// a silently truncated report looks exactly like a complete one. expandSeries
// returns the array; this companion answers "was that all of them?".
export function wouldTruncate(rule, windowStart, windowEnd) {
  // Not routed through the core (#216): no production code calls it, and it is
  // one line over `expandSeries`, whose implementation is the core's.
  return expandSeries(rule, windowStart, windowEnd).length >= MAX_OCCURRENCES;
}

// Expand `rule` into the occurrence dates falling within [windowStart, windowEnd].
//
// rule: {
//   startDate: 'YYYY-MM-DD',
//   frequency: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'biweekly' (legacy),
//   interval: number,            // every N periods
//   endDate?: 'YYYY-MM-DD'|null, // repeat until, inclusive; null => indefinite
//   exceptions?: string[],       // dates skipped entirely (removed instances)
//   byWeekday?: 0-6|null,        // 0 Sun .. 6 Sat, with weekOfMonth below
//   weekOfMonth?: 1-5|-1|null,   // "the second Monday"; -1 is the last
// }
//
// The last two are MMS's other monthly anchor. Present together on a rule whose
// stride is months, they replace the day-of-month walk with an ordinal-weekday
// one; absent, the rule walks day-of-month. An ordinal of 5 can produce *no*
// date in a month, which is why the walk steps by month rather than from the
// last date it found.
//
// A series is bounded by a date, not by a number of occurrences: MMS's
// recurrence offers "Repeat indefinitely" or a "Repeat Until" date, and a studio
// thinking "until the end of term" should not have to count weeks. A one-time
// event is a series whose end date is its start date.
export function expandSeries(rule, windowStart, windowEnd) {
  return coreSync().expandSeries(
    ...flat(rule), String(windowStart ?? ''), String(windowEnd ?? ''),
  );
}

// Does this rule produce a lesson on `dateISO`?
//
// Asked by the range query when a lesson has been *moved*. A moved lesson is an
// override keyed by its rule date carrying the date it was moved to, and a rule
// that has been edited since may no longer produce that rule date at all — in
// which case the override is unreachable and must not be drawn on the day it
// names.
//
// A one-day expansion rather than an inversion of the step arithmetic, so it
// cannot disagree with the walk about end dates, monthly clamping and
// exceptions. It runs only for the overrides that carry a moved date.
export function occursOn(rule, dateISO) {
  if (!dateISO) return false;
  return coreSync().occursOn(...flat(rule), String(dateISO));
}

// Do these two versions of a rule put lessons on the same dates?
//
// Asked before an edit is applied to a rule in place, because an override is
// keyed by the rule date it belongs to: move the dates a rule produces and its
// overrides are orphaned, keyed to days nothing will ever ask about again.
//
// Only where the run starts and how often it repeats decide the date set.
// `endDate` and `exceptions` subtract from it, and every date that survives
// stays keyed exactly where it was, so neither is a reschedule.
//
// The repetition is compared as the normalised pattern, never the raw fields,
// so biweekly-every-1 and weekly-every-2 are one fortnight. The nth-weekday
// anchor is compared too: "monthly on day 14" and "monthly on the second
// Monday" have the same stride and almost no dates in common.
export function producesSameDates(before, after) {
  return coreSync().producesSameDates(
    scalars(before), exceptionsOf(before), scalars(after), exceptionsOf(after),
  );
}

// Does this rule still put lessons on days after `dateISO`?
//
// Asked twice by an edit that shortens a run: by the form, which warns before
// Save that entries past the new "Repeat Until" will be deleted, and by the
// save itself, working out which overrides to cull. Both ask here so the
// warning cannot promise something the save does not then do.
//
// An indefinite rule answers yes without expanding anything: it has
// occurrences after every date there is.
export function producesEntriesAfter(rule, dateISO) {
  if (!dateISO) return false;
  return coreSync().producesEntriesAfter(...flat(rule), String(dateISO));
}

// Where an edit's overrides stop being reachable because the run got shorter —
// or null if it did not get shorter.
//
// A different question from `producesSameDates`, which asks whether the dates
// *moved* (orphaning every override, answered with `dropAllOverrides`). This
// asks whether the run got *shorter*, which strands only the excluded tail —
// `dropOverridesFrom` and this date.
//
// MMS deletes that tail and warns while you type ("You will be deleting any
// entry after 9/21/2026"); a deleted entry's override does not come back when
// the run is extended again.
//
// "Repeat Until" is inclusive — a lesson falls on the end date itself — so the
// cut is the day *after* it.
export function shortenedFrom(before, after) {
  return coreSync().shortenedFrom(
    scalars(before), exceptionsOf(before), scalars(after), exceptionsOf(after),
  ) ?? null;
}

// Does this edit change how the series repeats, rather than what one entry of
// it looks like?
//
// The start date is not part of it. On any occurrence but the first, the form's Date field addresses that lesson rather than the rule, and
// moving one lesson is exactly the scope question MMS asks about. Frequency,
// interval and the repeat bound have no per-entry meaning at all, which is why
// MMS asks nothing when they change.
export function changesRecurrence(before, after) {
  return coreSync().changesRecurrence(
    scalars(before), exceptionsOf(before), scalars(after), exceptionsOf(after),
  );
}
